import { createApp } from '@backstage/frontend-defaults';
import { convertLegacyAppOptions } from '@backstage/core-compat-api';
import { SignInPage } from '@backstage/core-components';
import { githubAuthApiRef, discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import type { IdentityApi } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider, themes } from '@backstage/theme';
import { skyralDarkTheme, skyralLightTheme } from './themes/skyralLightTheme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { apis, auth0AuthApiRef } from './apis';
import { setTokenCookie } from './cookieAuth';
import { navModule } from './modules/nav';
import { homePageModule } from './modules/home/index';

// Explicit plugin imports for new frontend system
import authPlugin from '@backstage/plugin-auth';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import capabilitiesPlugin from '@internal/backstage-plugin-capabilities';
import homePlugin from '@backstage/plugin-home/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import techdocsMermaidModule from 'backstage-plugin-techdocs-addon-mermaid/alpha';

const legacyOptions = convertLegacyAppOptions({
  apis,
  components: {
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);
      return (
        <SignInPage
          {...props}
          providers={[
            {
              id: 'auth0-auth-provider',
              title: 'General Login',
              message: 'Sign in using Google',
              apiRef: auth0AuthApiRef,
            },
            {
              id: 'github-auth-provider',
              title: 'Engineering Login',
              message: 'Sign in using GitHub',
              apiRef: githubAuthApiRef,
            }
          ]}
          title="Select a sign-in method"
          align="center"
          onSignInSuccess={async (identityApi: IdentityApi) => {
            setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              identityApi,
            );
            props.onSignInSuccess(identityApi);
          }}
        />
      );
    },
  },
  themes: [{
    id: 'builtin-light-theme',
    title: 'Builtin Light Theme',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={themes.light} children={children} />
    ),
  },
  {
    id: 'builtin-dark-theme',
    title: 'Builtin Dark Theme',
    variant: 'light',
    icon: <DarkIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={themes.dark} children={children} />
    ),
  },
  {
    id: 'skyral-light-theme',
    title: 'Skyral Light Theme',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={skyralLightTheme} children={children} />
    ),
  },
  {
    id: 'skyral-dark-theme',
    title: 'Skyral Dark Theme',
    variant: 'dark',
    icon: <DarkIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={skyralDarkTheme} children={children} />
    ),
  }],
});

export default createApp({
  features: [
    legacyOptions,
    navModule,
    homePageModule,
    authPlugin,
    catalogPlugin,
    homePlugin,
    techdocsPlugin,
    techdocsMermaidModule,
    searchPlugin,
    userSettingsPlugin,
    scaffolderPlugin,
    apiDocsPlugin,
    catalogGraphPlugin,
    capabilitiesPlugin,
  ],
});
